import { useState, useEffect } from "react";
import { Search, Check, ChevronDown, FolderKanban } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui-components/ui/dropdown-menu";
import { PROJECT_ICON_MAP } from "@/lib/constants";
import { getProjectColorClasses } from "@/lib/utils/formatting";
import type { Project } from "../../../types/dashboard";
import { useFilters } from "@/hooks/useFilters";

interface MultiSelectProjectDropdownProps {
  projects: Project[];
  className?: string;
  triggerClassName?: string;
  includeAllOption?: boolean;
  allOptionLabel?: string;
}

export function MultiSelectProjectDropdown({
  projects = [],
  includeAllOption = true,
  allOptionLabel = "All Projects",
  triggerClassName,
  className,
}: MultiSelectProjectDropdownProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { projectIds, toggleProjectId, resetProjectsFilter } = useFilters();

  // Add "All Projects" option if requested
  const allProjects = includeAllOption
    ? [
        {
          id: "all",
          name: allOptionLabel,
          totalMarkdownSize: 0,
          sessionCount: 0,
          lastUpdated: "",
          color: "gray",
          icon: "FolderKanban",
        },
        ...projects,
      ]
    : projects;

  // Check if "all" is selected
  const isAllSelected = projectIds.includes("all");

  // Get display text for trigger button
  const getDisplayText = () => {
    if (isAllSelected || projectIds.length === 0) {
      return allOptionLabel;
    }
    if (projectIds.length === 1) {
      const project = allProjects.find((p) => p.id === projectIds[0]);
      return project?.name || "";
    }
    return `${projectIds.length} projects`;
  };

  // Get the icon/color to display
  const getDisplayIcon = () => {
    if (isAllSelected || projectIds.length === 0 || projectIds.length > 1) {
      return null;
    }
    const project = allProjects.find((p) => p.id === projectIds[0]);
    if (!project || project.id === "all") return null;

    const colorClasses = getProjectColorClasses(project.color);
    const IconComponent =
      project.icon &&
      PROJECT_ICON_MAP[project.icon as keyof typeof PROJECT_ICON_MAP]
        ? PROJECT_ICON_MAP[project.icon as keyof typeof PROJECT_ICON_MAP]
        : FolderKanban;

    // Ensure IconComponent is valid before rendering
    if (!IconComponent) return null;

    return (
      <div
        className={`w-4 h-4 rounded ${colorClasses.bg} ${colorClasses.border} border flex items-center justify-center flex-shrink-0`}
      >
        <IconComponent className={`w-2.5 h-2.5 ${colorClasses.text}`} />
      </div>
    );
  };

  // Handle selection changes
  const handleSelect = (projectId: string) => {
    try {
      if (projectId === "all") {
        // If selecting "all", clear other selections
        resetProjectsFilter();
      } else {
        // Remove "all" if it was selected
        toggleProjectId(projectId);
      }
    } catch (error) {
      console.error("Error in handleSelect:", error);
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    resetProjectsFilter();
    setSearchQuery("");
  };

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Filter projects based on search
  const filteredProjects = allProjects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count selected (excluding "all")
  const selectedCount = isAllSelected ? 0 : projectIds.length;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            triggerClassName +
            `
            ${
              projectIds.length > 0
                ? "border border-border text-foreground hover:border-border hover:bg-muted"
                : "text-muted-foreground hover:bg-muted"
            }
          `
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {getDisplayIcon() || <FolderKanban className="w-3.5 h-3.5" />}
          <span>{getDisplayText()}</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={`${className || "w-[250px]"} z-[100]`}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Prevent closing when clicking on filter menu elements
          const target = e.target as HTMLElement;
          if (target.closest(".filter-menu-container")) {
            e.preventDefault();
          }
        }}
      >
        {allProjects.length > 10 && (
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-[#01B9D9] focus:border-transparent"
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
        )}

        {selectedCount > 0 && (
          <div className="border-b border-border p-3 flex items-center justify-between space-x-6">
            <span className="text-xs text-muted-foreground">
              {selectedCount} project{selectedCount !== 1 ? "s" : ""} selected
            </span>
            <button
              onMouseDown={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-xs text-[#01B9D9] hover:text-[#0095B6] font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="max-h-[300px] overflow-y-auto">
          {filteredProjects.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">No projects found</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try a different search
              </p>
            </div>
          ) : (
            filteredProjects.map((project) => {
              const isSelected =
                project.id === "all"
                  ? isAllSelected
                  : projectIds.includes(project.id);
              return (
                <button
                  key={project.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleSelect(project.id);
                    // Only close if selecting "all"
                    if (project.id === "all") {
                      setIsOpen(false);
                    }
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center justify-between group"
                >
                  <span className="flex items-center gap-2">
                    {project.id !== "all" &&
                      (() => {
                        try {
                          const colorClasses = getProjectColorClasses(
                            project.color
                          );
                          const IconComponent =
                            project.icon &&
                            PROJECT_ICON_MAP[
                              project.icon as keyof typeof PROJECT_ICON_MAP
                            ]
                              ? PROJECT_ICON_MAP[
                                  project.icon as keyof typeof PROJECT_ICON_MAP
                                ]
                              : FolderKanban;

                          if (!IconComponent) return null;

                          return (
                            <div
                              className={`w-4 h-4 rounded ${colorClasses.bg} ${colorClasses.border} border flex items-center justify-center flex-shrink-0`}
                            >
                              <IconComponent
                                className={`w-2.5 h-2.5 ${colorClasses.text}`}
                              />
                            </div>
                          );
                        } catch (error) {
                          console.error("Error rendering project icon:", error);
                          return null;
                        }
                      })()}
                    <span
                      className={
                        isSelected
                          ? "font-medium text-[#01B9D9]"
                          : "text-foreground"
                      }
                    >
                      {project.name}
                    </span>
                  </span>
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-[#01B9D9]" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
